import { Grid } from '@material-ui/core';
import { Formik } from 'formik';
import React from 'react';
import { Button, FormText, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import Toastr from 'toastr';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { User, UserPermissions } from '../types';
import axiosClient from '../utils/axiosClient';

const AdminUserModal: React.FC<{
  type: 'Create' | 'Edit';
  userData?: User;
  open: boolean;
  onClose: () => void
}> = ({ type, userData, open, onClose }) => {
  const { data: currentUserData } = useCurrentUser()
  const rolesToShow = [UserPermissions.read, UserPermissions.write, UserPermissions.bulkqna, UserPermissions.dataanalysis, UserPermissions.betauser, UserPermissions.translator, UserPermissions.internalLLM]
  // if user editing own user, show all roles
  if (currentUserData && currentUserData.email === userData?.email) {
    rolesToShow.push(UserPermissions.admin)
  }

  return (
    <Modal isOpen={open} onClose={onClose}>
      <ModalHeader>{type} User</ModalHeader>
      <Formik
        initialValues={{
          id: userData?.id || '',
          name: userData?.name || '',
          email: userData?.email || '',
          roles: userData?.roles || [],
        }}
        validate={values => {
          const errors: Record<string, string | undefined> = {};
          const {
            name,
            email,
            roles
          } = values;
          if (name.length <= 0) {
            errors.name = 'Name is required.'
          }
          if (!email.toLowerCase().endsWith('evalueserve.com')) {
            errors.email = 'Emails must have a domain of evalueserve.com'
          }
          if (currentUserData?.email !== userData?.email && roles.includes(UserPermissions.admin)) {
            errors.roles = 'You cannot assign admin roles!'
          }
          return errors
        }}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const resp = await axiosClient.request<{ error: boolean, msg: string }>({
              url: type === 'Edit' ? 'admin/updateUser' : 'admin/createUser',
              method: type === "Edit" ? "PATCH" : "POST",
              headers: {
                'Content-Type': 'application/json'
              },
              data: values
            })
            const { error, msg } = resp.data
            if (error) throw new Error(msg)
            Toastr.success(type === 'Create' ? 'User Created!' : 'User updated!')
            setTimeout(() => window.location.reload(), 1000)
          } catch (error) {
            console.error(error)
            // Toastr.error(error.message)
          } finally {
            setSubmitting(false)
          }
        }}
      >
        {
          ({
            handleChange,
            handleBlur,
            submitForm,
            isSubmitting,
            values,
            errors,
          }) => (
            <>
              <ModalBody>
                <div className="mb-1">
                  <div>Name</div>
                  <Input
                    css=""
                    className="mt-1"
                    placeholder="Name"
                    type="text"
                    name="name"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.name}
                    valid={!errors.name}
                  />
                  {
                    errors.name &&
                    <FormText color="red">
                      {errors.name}
                    </FormText>
                  }
                </div>

                <div className="mb-1">
                  <div>Email</div>
                  <Input
                    css=""
                    className="mt-1"
                    placeholder="Email"
                    type="email"
                    name="email"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    valid={!errors.email}
                  />
                  {
                    errors.email &&
                    <FormText color="red">
                      {errors.email}
                    </FormText>
                  }
                </div>

                <div className="mb-1">
                  <div className="font-bold mb-1 text-sm">
                    Roles
                  </div>
                  <Grid container style={{ marginLeft: '21px' }}>
                    {
                      rolesToShow.map((perm) => (
                        <Grid xs={3} item>
                          <Label check key={perm}>
                            <Input value={perm} css="" type="checkbox" name="roles" onChange={handleChange} onBlur={handleBlur} checked={values.roles.includes(perm)} />
                            <span className="ml-2">{perm}</span>
                          </Label>
                        </Grid>
                      ))
                    }
                  </Grid>
                  {
                    errors.roles &&
                    <FormText color="red">
                      {errors.roles}
                    </FormText>
                  }
                </div>
              </ModalBody>
              <ModalFooter>
                <Button color="primary" onClick={() => {
                  if (Object.keys(errors).length > 0 || isSubmitting) return
                  if (window.confirm(`${type} user?`)) {
                    submitForm()
                  }
                }}>
                  {type}
                </Button>
                <Button onClick={onClose}>
                  Quit
                </Button>
              </ModalFooter>
            </>
          )
        }
      </Formik>
    </Modal>
  )
}

export default AdminUserModal

